import React, { useEffect, useState } from "react";
import { functionsService } from "@/services/functionsService";
import { Card } from "@/components/ui/Card";

interface StockQuoteProps {
  ticker: string;
}

export const StockQuote: React.FC<StockQuoteProps> = ({ ticker }) => {
  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuote();
  }, [ticker]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const result = await functionsService.getCompanyQuote(ticker);
      if (result.success && result.quote) {
        setQuote(result.quote);
      }
    } catch (error: any) {
      console.error("Failed to load quote:", error);
      // Show error message to user
      if (error?.code === "unauthenticated") {
        console.warn("Please sign in to view real-time quotes");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
        </div>
      </Card>
    );
  }

  if (!quote) {
    return (
      <Card className="p-6">
        <p className="body-text text-gray-500 text-center">
          Quote data not available. Please try again or check if the ticker is
          correct.
        </p>
      </Card>
    );
  }

  const changePercent = quote.changePercentage || quote.changesPercentage || 0;
  const isPositive = changePercent >= 0;
  const changeColor = isPositive ? "text-green-600" : "text-red-600";

  return (
    <Card className="p-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">Price</p>
          <p className="h1 text-2xl font-header-bold">
            ${quote.price?.toFixed(2) || "N/A"}
          </p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">Change</p>
          <p className={`h1 text-xl font-header-bold ${changeColor}`}>
            {isPositive ? "+" : ""}
            {quote.change?.toFixed(2) || "N/A"} ({isPositive ? "+" : ""}
            {changePercent?.toFixed(2) || "N/A"}%)
          </p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">Market Cap</p>
          <p className="body-text text-lg font-header-bold">
            {quote.marketCap
              ? `$${(quote.marketCap / 1e9).toFixed(2)}B`
              : "N/A"}
          </p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">Volume</p>
          <p className="body-text text-lg font-header-bold">
            {quote.volume ? (quote.volume / 1e6).toFixed(2) + "M" : "N/A"}
          </p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">52W High</p>
          <p className="body-text text-lg">
            ${quote.yearHigh?.toFixed(2) || "N/A"}
          </p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">52W Low</p>
          <p className="body-text text-lg">
            ${quote.yearLow?.toFixed(2) || "N/A"}
          </p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">P/E Ratio</p>
          <p className="body-text text-lg">{quote.pe?.toFixed(2) || "N/A"}</p>
        </div>
        <div>
          <p className="body-text text-sm text-gray-500 mb-1">EPS</p>
          <p className="body-text text-lg">${quote.eps?.toFixed(2) || "N/A"}</p>
        </div>
      </div>
    </Card>
  );
};
